import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { confirmDiscardIfDirty } from "./confirmDiscardIfDirty";

const DISMISS_LABEL = /^(close|cancel|exit|έξοδος|κλείσιμο|άκυρο)$/i;

/**
 * Drop-in Modal that blocks accidental discard:
 * - backdrop click / Esc do not close
 * - Close / Cancel / X confirm if the form was edited
 *
 * Pass `isDirty` to override auto-detection (input/change inside the dialog).
 * After a successful save, call your close function directly (do not rely on Cancel).
 */
export default function GuardedModal({
  isDirty,
  onHide,
  children,
  backdrop,
  keyboard,
  dialogClassName,
  show,
  enforceFocus,
  ...rest
}) {
  const [autoDirty, setAutoDirty] = useState(false);
  const showRef = useRef(show);
  const dirtyRef = useRef(false);
  const onHideRef = useRef(onHide);

  const dialogClass = useMemo(
    () => `guarded-modal-${Math.random().toString(36).slice(2, 10)}`,
    []
  );
  const mergedDialogClass = [dialogClassName, dialogClass].filter(Boolean).join(" ");

  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  const dirty = typeof isDirty === "boolean" ? isDirty : autoDirty;
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const requestHide = useCallback(async () => {
    if (!(await confirmDiscardIfDirty(dirtyRef.current))) return;
    if (typeof onHideRef.current === "function") onHideRef.current();
  }, []);

  useEffect(() => {
    showRef.current = show;
    if (!show) {
      setAutoDirty(false);
      return undefined;
    }
    setAutoDirty(false);

    const markDirty = (e) => {
      if (!showRef.current) return;
      const t = e?.target;
      if (t && typeof t.closest === "function" && t.closest(`.${dialogClass}`)) {
        setAutoDirty(true);
      }
    };

    document.addEventListener("input", markDirty, true);
    document.addEventListener("change", markDirty, true);
    document.addEventListener("keyup", markDirty, true);
    return () => {
      document.removeEventListener("input", markDirty, true);
      document.removeEventListener("change", markDirty, true);
      document.removeEventListener("keyup", markDirty, true);
    };
  }, [show, dialogClass]);

  // Route footer Close/Cancel clicks through the same confirm path as the header X
  useEffect(() => {
    if (!show) return undefined;

    const onClickCapture = (e) => {
      const root = document.querySelector(`.${dialogClass}`);
      if (!root || !root.contains(e.target)) return;

      const el = e.target.closest?.("button, [type='button'], .btn, .ui.button");
      if (!el || !root.contains(el)) return;

      // Header X already goes through Modal onHide → requestHide
      if (el.classList.contains("btn-close") || el.getAttribute("data-dismiss") === "modal") {
        return;
      }

      const label = (el.textContent || el.getAttribute("aria-label") || "").trim();
      if (!DISMISS_LABEL.test(label)) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      requestHide();
    };

    // Delay bind so the dialog node exists in the portal
    const timer = window.setTimeout(() => {
      document.addEventListener("click", onClickCapture, true);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [show, dialogClass, requestHide]);

  return (
    <Modal
      {...rest}
      show={show}
      onHide={requestHide}
      backdrop={backdrop === undefined ? "static" : backdrop}
      keyboard={keyboard === undefined ? false : keyboard}
      // Allow SweetAlert confirm dialogs to receive focus/clicks above this modal
      enforceFocus={enforceFocus === undefined ? false : enforceFocus}
      dialogClassName={mergedDialogClass}
    >
      {children}
    </Modal>
  );
}
